'use server';

import { apiRequest } from '@/utils/api-request';

export interface Class {
  id: number;
  uuid: string;
  class_name: string;
  section?: string;
  max_strength?: number;
  description?: string;
  student_count?: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ClassPagination {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  itemsPerPage: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

interface GetClassesResponse {
  success: boolean;
  data: {
    data: Class[];
    pagination: ClassPagination;
    total: number;
    page: number;
    limit: number;
  };
  message?: string;
}

interface CreateClassPayload {
  class_name: string;
  section?: string;
  max_strength?: number;
  description?: string;
}

interface UpdateClassPayload {
  class_name?: string;
  section?: string;
  max_strength?: number;
  description?: string;
  is_active?: boolean;
}

/**
 * Fetch classes with pagination and search
 */
export async function getClasses(params: {
  page?: number;
  limit?: number;
  search?: string;
  sort_column?: string;
  sort_order?: 'ASC' | 'DESC';
}): Promise<GetClassesResponse> {
  try {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.sort_column) queryParams.append('sort_column', params.sort_column);
    if (params.sort_order) queryParams.append('sort_order', params.sort_order);

    const response = await apiRequest(
      `/api/parent-app/admin/classes?${queryParams.toString()}`,
      { method: 'GET' }
    );

    return response as GetClassesResponse;
  } catch (error: any) {
    return {
      success: false,
      data: { data: [], pagination: {} as ClassPagination, total: 0, page: 1, limit: 10 },
      message: error.message || 'Failed to fetch classes'
    };
  }
}

/**
 * Create a new class
 */
export async function createClass(payload: CreateClassPayload) {
  try {
    const response = await apiRequest('/api/parent-app/admin/classes', {
      method: 'POST',
      body: JSON.stringify(payload)
    });

    return response;
  } catch (error: any) {
    throw new Error(error.message || 'Failed to create class');
  }
}

/**
 * Update a class
 */
export async function updateClass(id: number, payload: UpdateClassPayload) {
  try {
    const response = await apiRequest(`/api/parent-app/admin/classes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload)
    });

    return response;
  } catch (error: any) {
    throw new Error(error.message || 'Failed to update class');
  }
}

/**
 * Delete a class
 */
export async function deleteClass(id: number) {
  try {
    const response = await apiRequest(`/api/parent-app/admin/classes/${id}`, {
      method: 'DELETE'
    });

    return response;
  } catch (error: any) {
    throw new Error(error.message || 'Failed to delete class');
  }
}
